import React, { useMemo } from 'react';
import classNames from 'classnames';

import { Popper, Tooltip, Placements } from '@nextroll/ar-style-base';


interface IProps {
    validate?: (value: string) => boolean; // determine whether a chip has error
    include?: (value: string) => boolean; // determine whether a chip has the included label
    exclude?: (value: string) => boolean; // determine whether a chip has the excluded label
    error?: string; // error message shown in the tooltip
    onDelete?: (e: React.MouseEvent, label: string) => void;
}

const Chip = (props: IProps & { label: string }) => {
    const { label, validate, include, exclude, error, onDelete } = props;
    const valid = !validate || validate?.(label);

    // "included" and "excluded" are currently used in the contact filters > job title input
    const included = valid && include?.(label);
    const excluded = valid && exclude?.(label);

    /**
     * render the core chip component
     */
    const chip = useMemo(() => (
        <div className='ar-search-selector-label-container'>
            <div
                data-testid='workflow-chip'
                className={classNames('ar-search-selector-label', {
                    // eslint-disable-next-line no-useless-computed-key
                    ['ar-search-selector-label-error']: !valid,
                    // eslint-disable-next-line no-useless-computed-key
                    ['ar-search-selector-label-include']: included,
                    // eslint-disable-next-line no-useless-computed-key
                    ['ar-search-selector-label-exclude']: excluded,
                    // eslint-disable-next-line no-useless-computed-key
                    ['ar-search-selector-label-disabled']: !onDelete,
                })}
            >
                {excluded && <i className='fas fa-ban mr-1' aria-label='workflow-chip-excluded' aria-hidden />}
                <span>{label}</span>
                {onDelete && <i role='button' className='fas fa-times' onClick={e => onDelete?.(e, label)} />}
            </div>
        </div>
    ), [label, valid, included, excluded, onDelete]);

    if (valid) {
        return chip;
    }

    /**
     * wrap the chip with the error tooltip if applicable
     */
    const tooltip = <Tooltip placement={Placements.BOTTOM} className='workflow-node-panel-chips-tooltip'>{error}</Tooltip>;
    return <Popper hoverContent={tooltip} breakOutOfOverflow>{chip}</Popper>;
};

/**
 * Note: for both labels and error, please translate them externally (i18n) if necessary, before feeding them into here
 */
export const Chips = (props: IProps & { labels: string[]; className?: string }) => {
    const { labels, className } = props;

    if (labels.length <= 0) {
        return null;
    }
    return (
        <div data-testid='chips' className={`workflow-node-panel-chips ar-search-selector${className ? ` ${className}` : ''}`}>
            <div className='ar-search-selector-search-container'>
                {labels.map(label => <Chip key={`workflow-chip-${label}`} {...props} label={label} />)}
            </div>
        </div>
    );
};
